import { colors } from 'styles/colors';
import { IStageMetaData } from '../models/myOpenRequisition.model';
import { IObjectData } from '../components/candidateApplication/candidateApplicationDetails.helper';
import { ICandidateApplicationDataObject } from '../components/candidateApplication/candidateApplicationDetails';
import { resumeFileTypes } from '../components/candidateApplication/candidateModalResume';
import moment from 'moment';
import { DateTime } from 'luxon';
import { calculatePeriod } from 'utilities/formatDate';

export const getStageColor = (
  stageName: string,
  stagesMeta: IStageMetaData[]
) => {
  const stepColors = [
    colors.tertiary.yellow,
    colors.tertiary.blue,
    colors.tertiary.green,
  ];
  const index = stagesMeta.findIndex(
    (stageMeta) => stageMeta.name === stageName
  );
  return stepColors[index % stepColors.length];
};

export const calculateTimeInStep = (timestamp: string) => {
  const [dateStr, timeStr] = timestamp.split('T');
  const [year, month, day] = dateStr.split('-').map(Number);
  const [timeWithoutMilliseconds] = timeStr.split('.');
  const [hour, minute, second] = timeWithoutMilliseconds.split(':').map(Number);
  const timestampDate: Date = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second)
  );
  const currentDate: Date = new Date();
  const timeDifference: number =
    currentDate.getTime() - timestampDate.getTime();
  const dayDifference: number = Math.floor(timeDifference / (1000 * 3600 * 24));
  const formatTimeDifference = (difference: number) => {
    if (difference === 0) return `${difference} day`;
    else if (difference === 1) return `${difference} day`;
    else return `${difference} days`;
  };
  const formattedTimeDifference = formatTimeDifference(dayDifference);
  return formattedTimeDifference;
};

export const calculateDaysinStep = (timestamp: string) => {
  const [dateStr, timeStr] = timestamp.split(' ');
  const [month, day, year] = dateStr.split('/').map(Number);
  const [timeWithoutMilliseconds] = timeStr.split('.');
  const [hour, minute, second] = timeWithoutMilliseconds.split(':').map(Number);
  const timestampDate: Date = new Date(
    Date.UTC(year, month - 1, day, hour, minute, second)
  );
  const currentDate: Date = new Date();
  const timeDifference: number =
    currentDate.getTime() - timestampDate.getTime();
  const dayDifference: number = Math.floor(timeDifference / (1000 * 3600 * 24));
  return dayDifference;
};

export const mapDataToLabelAndValue = (data: IObjectData) => {
  const mappedData: ICandidateApplicationDataObject = {};
  Object.keys(data)?.map((key: string) => {
    const val = Object.keys(data[key])?.map((childKey) => {
      return { label: childKey, value: data[key][childKey] };
    });
    mappedData[key] = val;
  });
  return mappedData;
};

export const customEventSubscribe = (
  eventName: string,
  listener: EventListenerOrEventListenerObject
) => {
  document.addEventListener(eventName, listener);
};

export const customEventUnsubscribe = (
  eventName: string,
  listener: EventListenerOrEventListenerObject
) => {
  document.removeEventListener(eventName, listener);
};

export const customEventPublish = (eventName: string, data: any) => {
  const event = new CustomEvent(eventName, { detail: data });
  document.dispatchEvent(event);
};

export const base64ResumeToArrayBuffer = (
  source?: string,
): ArrayBuffer | undefined => {
  if (!source) return undefined;
  let base64 = source.startsWith('data:')
    ? source.split(',')[1] || ''
    : source;
  base64 = base64.replace(/\s/g, '');
  const remainder = base64.length % 4;
  if (remainder) {
    base64 += '='.repeat(4 - remainder);
  }
  try {
    const binaryString = atob(base64);
    const buffer = new ArrayBuffer(binaryString.length);
    const bufferView = new Uint8Array(buffer);
    for (let i = 0; i < binaryString.length; i++) {
      bufferView[i] = binaryString.charCodeAt(i);
    }
    return buffer;
  } catch {
    return undefined;
  }
};

export const decodeBase64URL = (base64?: string, fileName?: string) => {
  if (!base64) return undefined;
  if (base64.startsWith('data:')) {
    return base64;
  }
  if (/^https?:\/\//i.test(base64)) {
    return base64;
  }
  for (const prefix in resumeFileTypes) {
    if (base64.startsWith(prefix)) {
      return `${(resumeFileTypes as any)[prefix]},${base64}`;
    }
  }
  const lowerFileName = fileName?.toLowerCase() ?? '';
  if (lowerFileName.endsWith('.docx') || lowerFileName.endsWith('.doc')) {
    return `data:application/vnd.openxmlformats-officedocument.wordprocessingml.document;base64,${base64}`;
  }
  if (lowerFileName.endsWith('.pdf')) {
    return `data:application/pdf;base64,${base64}`;
  }
  return base64;
};

export const truncateComments = (
  comments: string,
  isShowMore: boolean,
  maxLength: number
) => {
  if (comments?.length > maxLength && !isShowMore)
    return comments?.substring(0, maxLength) + '...';
  else return comments;
};

export const formatResumeFilename = (fileName: string) => {
  if (!fileName) return '';
  return `${fileName}`;
};

export const formatToOneDecimal = (updatedRating: string) => {
  if (isNaN(+updatedRating)) return 0;
  return (Math.floor(Number(updatedRating!) * 10) / 10).toFixed(1);
};
export interface IJobLocationAddress {
  city?: string | null;
  state?: string | null;
  country?: string | null;
}

export const formatJobLocation = (
  address: IJobLocationAddress | null | undefined
): string => {
  if (!address) return '';

  const { city, state, country } = address;

  const locationParts: string[] = [];

  if (city) locationParts.push(city);
  if (state) locationParts.push(state);
  if (country) locationParts.push(country);

  return locationParts.join(', ') || '';
};

export const calculateEachTotalWorkExperience = (item: any) => {
  // Parse dates using Luxon's DateTime
  const startDate = DateTime.fromISO(item?.startDate);
  const endDate = item?.IsCurrent || !item?.endDate
    ? DateTime.now()
    : DateTime.fromISO(item?.endDate);

  // Calculate total months since epoch for both dates
  const startTotalMonths = (startDate.year * 12) + startDate.month;
  const endTotalMonths = (endDate.year * 12) + endDate.month;

  // Calculate difference with inclusive counting (+1 to include both start and end months)
  const totalMonths = endTotalMonths - startTotalMonths + 1;

  // Convert to years and months
  return {
    years: Math.floor(totalMonths / 12),
    months: totalMonths % 12
  };
};

export const formatWorkExperienceDuration = (
  years: number,
  months: number,
  translate: (key: string) => string
): string => {
  const yearText = years === 1
    ? translate('year')
    : translate('years');

  const monthText = months === 1
    ? translate('month')
    : translate('months');

  return `${years} ${yearText} ${months} ${monthText}`;
};
